/**
 * Cloud Storage helpers.
 *
 * Project photos live under `projects/{projectId}/photos/{filename}`. The
 * browser never talks to Storage (`storage.rules` is deny-all, #66 P0): uploads
 * POST to kronos-api (`services/kronos-api/src/actions/uploadPhoto.ts`), which
 * checks project-manager / elevated role and writes with the Admin SDK.
 *
 * Phase 4 uses this from the project creation wizard (Step 3) for a single
 * photo. The helper signature is unchanged from the previous direct-SDK version
 * so callers (and tests) are unaffected.
 */

import { apiPost } from '../data/restClient';

export interface UploadedPhoto {
  /** Absolute path in the bucket, e.g. `projects/abc123/photos/1712345-foo.jpg`. */
  path: string;
  /** Tokenised download URL (returned by the Cloud Function). Safe to persist in Firestore. */
  downloadURL: string;
}

/** Read a File into a base64 string (without the `data:...;base64,` prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is `data:<contentType>;base64,<payload>` — keep only the payload.
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export async function uploadProjectPhoto(
  projectId: string,
  file: File,
): Promise<UploadedPhoto> {
  const dataBase64 = await fileToBase64(file);
  const contentType = file.type || 'application/octet-stream';
  // Server persists the storage path + returns a same-origin /api proxy URL.
  return apiPost<UploadedPhoto>(`/projects/${projectId}/photo`, {
    fileName: file.name,
    contentType,
    dataBase64,
  });
}
